import { SupabaseClient } from "@supabase/supabase-js";
import { UserRepository } from "../repositories/user.repository";
import { UserProfileInsert } from "../entities/user";
import {
  AuthResponseDTO,
  SignUpDTO,
  SignInDTO,
  SignOutResponseDTO,
  SessionResponseDTO,
  GetUserResponseDTO,
  OAuthSignInDTO,
  OAuthResponseDTO,
} from "../dtos";
import { UserMapper } from "../mappers/user.mapper";

export class AuthService {
  private supabase: SupabaseClient;
  private userRepository: UserRepository;

  constructor(supabase: SupabaseClient) {
    this.supabase = supabase;
    this.userRepository = new UserRepository(supabase);
  }

  async signUp({
    email,
    password,
    metadata,
  }: SignUpDTO): Promise<AuthResponseDTO> {
    try {
      const { data: authData, error: authError } =
        await this.supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              ...metadata,
              display_name: metadata?.fullName || metadata?.full_name || email,
            },
          },
        });

      if (authError) {
        return { user: null, session: null, error: authError };
      }

      if (!authData.user) {
        return {
          user: null,
          session: null,
          error: new Error("User creation failed"),
        };
      }

      // Don't try to create/update or fetch the user profile here!
      // The trigger will create it in the users table.
      // Just return success and let the frontend redirect to verify-email
      
      return {
        user: null,
        session: authData.session,
        error: null,
      };
    } catch (error) {
      return {
        user: null,
        session: null,
        error: error as Error,
      };
    }
  }

  async signIn({ email, password }: SignInDTO): Promise<AuthResponseDTO> {
    try {
      const { data: authData, error: authError } =
        await this.supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (authError) {
        return { user: null, session: null, error: authError };
      }

      if (!authData.user) {
        return {
          user: null,
          session: null,
          error: new Error("Sign in failed"),
        };
      }

      const user = await this.userRepository.findByAuthId(authData.user.id);

      if (!user) {
        return {
          user: null,
          session: null,
          error: new Error("User not found"),
        };
      }

      return {
        user: UserMapper.toDTO(user),
        session: authData.session,
        error: null,
      };
    } catch (error) {
      return {
        user: null,
        session: null,
        error: error as Error,
      };
    }
  }

  async signOut(): Promise<SignOutResponseDTO> {
    const { error } = await this.supabase.auth.signOut();
    return { error };
  }

  async getSession(): Promise<SessionResponseDTO> {
    const { data, error } = await this.supabase.auth.getSession();
    return { session: data.session, error };
  }

  async getUser(): Promise<GetUserResponseDTO> {
    const { data, error } = await this.supabase.auth.getUser();

    if (error || !data.user) {
      return { user: null, error };
    }

    const user = await this.userRepository.findByAuthId(data.user.id);
    return { user: user ? UserMapper.toDTO(user) : null, error: null };
  }

  async signInWithOAuth({ provider, redirectTo }: OAuthSignInDTO): Promise<OAuthResponseDTO> {
    try {
      const { data, error } = await this.supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
          skipBrowserRedirect: false,
        },
      });

      if (error) {
        return { url: null, error };
      }

      return {
        url: data.url,
        error: null,
      };
    } catch (error) {
      return {
        url: null,
        error: error as Error,
      };
    }
  }

  async handleOAuthCallback(code: string): Promise<AuthResponseDTO> {
    try {
      const { data: authData, error: authError } =
        await this.supabase.auth.exchangeCodeForSession(code);

      if (authError) {
        return { user: null, session: null, error: authError };
      }

      if (!authData.user) {
        return {
          user: null,
          session: null,
          error: new Error("OAuth authentication failed"),
        };
      }

      // Check if user exists in our database
      let user = await this.userRepository.findByAuthId(authData.user.id);
      
      // If user doesn't exist, they will be created by the database trigger
      // But we should try to fetch them again after a short delay
      if (!user) {
        // Wait a moment for the trigger to create the user
        await new Promise(resolve => setTimeout(resolve, 500));
        user = await this.userRepository.findByAuthId(authData.user.id);
      }

      return {
        user: user ? UserMapper.toDTO(user) : null,
        session: authData.session,
        error: null,
      };
    } catch (error) {
      return {
        user: null,
        session: null,
        error: error as Error,
      };
    }
  }
}